import Papa from "papaparse";
import { supabase } from "../clients/supabaseClient";

/**
 * Aggressively cleans strings.
 * Returns an empty string if invalid to facilitate skipping logic.
 */
const formatName = (str: any): string => {
    if (!str || typeof str !== 'string') return "";
    let cleanStr = str.replace(/^[^a-zA-Z0-9]+/, '').trim();
    if (cleanStr.length === 0) return "";
    return cleanStr
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .split(' ')
        .filter(word => word.length > 0)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const CONCURRENCY = 12;

const normalizePriceToCents = (value: unknown): number => {
    const raw = String(value ?? "").trim();
    if (!raw) return 0;

    let cleaned = raw.replace(/[^0-9,.-]/g, "");

    // Handle comma decimals like "114,99" -> "114.99"
    if (cleaned.includes(",") && !cleaned.includes(".")) {
        cleaned = cleaned.replace(",", ".");
    }

    const numeric = Number.parseFloat(cleaned);
    if (!Number.isFinite(numeric)) return 0;

    return Math.round(numeric * 100);
};

async function runWithConcurrency<T, R>(
    items: T[],
    limit: number,
    worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let nextIndex = 0;

    async function runner() {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex++;
            results[currentIndex] = await worker(items[currentIndex], currentIndex);
        }
    }

    const runners = Array.from({ length: Math.min(limit, items.length) }, () => runner());
    await Promise.all(runners);
    return results;
}

export async function importProductsFromCSV(
    file: File,
    storeId: string,
    onProgress?: (progress: number) => void
): Promise<any> {
    const sessionMap = new Map<string, any>();
    const seenSkus = new Set<string>();

    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h) => h.trim().toLowerCase(),
            complete: async (results) => {
                const rows = results.data as any[];
                let created = 0, updated = 0, skipped = 0, failed = 0, unchanged = 0;
                const errors: string[] = [];

                const { data: deptMapRows, error: mapErr } = await supabase
                    .from("category_department_map")
                    .select("department_number, category_id");

                if (mapErr) throw mapErr;

                const departmentMap = new Map<number, string>(
                    (deptMapRows || []).map((m: any) => [Number(m.department_number), m.category_id])
                );

                const { data: otherCategoryRow, error: otherErr } = await supabase
                    .from("categories")
                    .select("id")
                    .eq("name", "Other")
                    .maybeSingle();

                if (otherErr) throw otherErr;

                const otherCategoryId = otherCategoryRow?.id;

                let processed = 0;

                const rowResults = await runWithConcurrency(rows, CONCURRENCY, async (row) => {
                    const rawSku = (row["productbarcode"] || "").toString().trim();
                    if (!rawSku || rawSku.includes("---")) {
                        return { status: "skipped" as const };
                    }

                    if (seenSkus.has(rawSku)) {
                        return { status: "skipped" as const };
                    }
                    seenSkus.add(rawSku);

                    const formattedName = formatName(row["description"] || "");
                    if (!formattedName) {
                        return { status: "skipped" as const };
                    }

                    try {
                        const deptNumberRaw = parseInt(String(row["departmentnumber"] ?? ""), 10);
                        const deptNumber = Number.isFinite(deptNumberRaw) ? deptNumberRaw : 0;
                        const safeDeptNumber = departmentMap.has(deptNumber) ? deptNumber : 0;

                        const incomingStock = parseFloat(row["stock"]) || 0;
                        const safePriceInCents = normalizePriceToCents(row["price"]);

                        const productData = {
                            sku: rawSku,
                            name: formattedName,
                            description: formattedName,
                            brand: row["brandname"]?.trim() || null,
                            product_code: row["productcode"]?.trim() || null,
                            pack_size: parseInt(row["packsize"]) || 1,
                            department_number: safeDeptNumber,
                            units: row["productunits"]?.trim() || null
                        };

                        let existingProd = sessionMap.get(rawSku);

                        if (!existingProd) {
                            const { data: dbProd, error: fetchErr } = await supabase
                                .from("products")
                                .select("id, name, description, brand, product_code, pack_size, department_number, units")
                                .eq("sku", rawSku)
                                .maybeSingle();

                            if (fetchErr) throw fetchErr;

                            if (dbProd) {
                                existingProd = dbProd;
                                sessionMap.set(rawSku, dbProd);
                            }
                        }

                        const productInfoChanged = !existingProd ||
                            existingProd.name !== productData.name ||
                            existingProd.description !== productData.description ||
                            existingProd.brand !== productData.brand ||
                            existingProd.product_code !== productData.product_code ||
                            existingProd.pack_size !== productData.pack_size ||
                            existingProd.department_number !== productData.department_number ||
                            existingProd.units !== productData.units;

                        const { data: pData, error: pErr } = await supabase
                            .from("products")
                            .upsert(productData, { onConflict: "sku" })
                            .select("id")
                            .single();

                        if (pErr) throw pErr;
                        const productId = pData.id;

                        const categoryId = departmentMap.get(deptNumber) ?? otherCategoryId;

                        const stockPromise = supabase
                            .from("products_store")
                            .select("stock")
                            .eq("product_id", productId)
                            .eq("store_id", storeId)
                            .maybeSingle();

                        const pricePromise = supabase
                            .from("product_prices")
                            .select("price")
                            .eq("product_id", productId)
                            .eq("store_id", storeId)
                            .is("valid_to", null)
                            .maybeSingle();

                        const [stockRes, priceRes] = await Promise.all([
                            stockPromise,
                            pricePromise
                        ]);

                        const stockChanged = !stockRes.data || stockRes.data.stock !== incomingStock;
                        const dbPrice = priceRes.data ? Number(priceRes.data.price) : null;
                        const priceChanged = dbPrice === null || dbPrice !== safePriceInCents;

                        const writes: PromiseLike<any>[] = [];

                        if (categoryId) {
                            writes.push(
                                supabase.from("category_products").upsert(
                                    {
                                        product_id: productId,
                                        category_id: categoryId
                                    },
                                    { onConflict: "product_id,category_id" }
                                )
                            );
                        }

                        if (stockChanged) {
                            writes.push(
                                supabase.from("products_store").upsert(
                                    {
                                        product_id: productId,
                                        store_id: storeId,
                                        stock: incomingStock
                                    },
                                    { onConflict: "product_id,store_id" }
                                )
                            );
                        }

                        if (priceChanged) {
                            const now = new Date().toISOString();

                            if (dbPrice !== null) {
                                writes.push(
                                    supabase
                                        .from("product_prices")
                                        .update({ valid_to: now })
                                        .is("valid_to", null)
                                        .eq("product_id", productId)
                                        .eq("store_id", storeId)
                                );
                            }

                            writes.push(
                                supabase.from("product_prices").insert({
                                    product_id: productId,
                                    store_id: storeId,
                                    price: safePriceInCents,
                                    valid_from: now
                                })
                            );
                        }

                        if (writes.length > 0) {
                            const results = await Promise.all(writes);
                            const writeError = results.find(r => r?.error)?.error;
                            if (writeError) throw writeError;
                        }

                        if (!existingProd) {
                            created++;
                            sessionMap.set(rawSku, { id: productId, ...productData });
                        } else if (productInfoChanged || stockChanged || priceChanged) {
                            updated++;
                            sessionMap.set(rawSku, { id: productId, ...productData });
                        } else {
                            unchanged++;
                        }

                        return { status: "ok" as const };
                    } catch (err: any) {
                        failed++;
                        return {
                            status: "failed" as const,
                            sku: rawSku,
                            error: err?.message || "Unknown error"
                        };
                    } finally {
                        processed++;
                        if (onProgress && processed % 10 === 0) {
                            onProgress((processed / rows.length) * 100);
                        }
                    }
                });

                for (const result of rowResults) {
                    if (result.status === "skipped") {
                        skipped++;
                    } else if (result.status === "failed") {
                        errors.push(`SKU ${result.sku}: ${result.error}`);
                    }
                }

                if (onProgress) onProgress(100);

                resolve({
                    total_rows: rows.length,
                    created,
                    updated,
                    unchanged,
                    skipped,
                    failed,
                    errors
                });
            },
            error: (err) => reject(err),
        });
    });
}